<template>
  <div class="game-container">
    <div class="game-content">
      <h1 class="game-title">Unscrambl</h1>

      <div v-if="!gameStarted" class="start-screen">
        <button @click="startGame" class="play-button" :disabled="words.length === 0">
          {{ words.length === 0 ? "Loading..." : "Play" }}
        </button>
      </div>

      <div v-else class="game-screen">
        <div class="word-display">{{ scrambledWord }}</div>

        <div v-if="hint" class="hint-text">Hint: First letter is "{{ hint }}"</div>

        <input v-model="userGuess" @keyup.enter="checkAnswer" type="text" class="input-field" placeholder="Type your answer..." autofocus />

        <div class="button-group">
          <button @click="checkAnswer" class="submit-button">Submit</button>
          <button @click="showHint" class="hint-button" :disabled="hintUsed">
            <img src="@/assets/lightbulb-line.svg" alt="Hint" class="hint-icon" />
          </button>
        </div>

        <div v-if="feedback" class="feedback" :class="feedbackClass">
          {{ feedback }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

const words = ref([]);
const currentWord = ref("");
const scrambledWord = ref("");
const userGuess = ref("");
const feedback = ref("");
const feedbackClass = ref("");
const gameStarted = ref(false);
const hint = ref("");
const hintUsed = ref(false);

const loadWords = async () => {
  try {
    const response = await fetch("/words.json");
    const data = await response.json();
    words.value = data.words.filter((word) => word.length >= 6 && word.length <= 10);
  } catch (error) {
    console.error("Error loading words:", error);
  }
};

const startGame = () => {
  if (words.value.length === 0) return;
  gameStarted.value = true;
  pickNewWord();
};

const pickNewWord = () => {
  if (words.value.length === 0) return;

  const randomIndex = Math.floor(Math.random() * words.value.length);
  currentWord.value = words.value[randomIndex];
  console.log("Current Word:", currentWord.value);
  scrambledWord.value = scrambleWord(currentWord.value).toUpperCase();
  userGuess.value = "";
  feedback.value = "";
  hint.value = "";
  hintUsed.value = false;
};

const scrambleWord = (word) => {
  const arr = word.split("");
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const scrambled = arr.join("");
  return scrambled === word ? scrambleWord(word) : scrambled;
};

const showHint = () => {
  if (!hintUsed.value && currentWord.value) {
    hint.value = currentWord.value.charAt(0).toUpperCase();
    hintUsed.value = true;
  }
};

const checkAnswer = () => {
  if (!userGuess.value.trim()) return;

  const isCorrect = userGuess.value.toLowerCase() === currentWord.value.toLowerCase();

  updateStats(isCorrect);

  if (isCorrect) {
    feedback.value = "Correct!";
    feedbackClass.value = "correct";
    setTimeout(() => {
      pickNewWord();
    }, 1500);
  } else {
    feedback.value = `Wrong! The correct answer was: ${currentWord.value}`;
    feedbackClass.value = "incorrect";
    setTimeout(() => {
      pickNewWord();
    }, 2500);
  }
};

const updateStats = (isCorrect) => {
  const stats = JSON.parse(localStorage.getItem("stats") || '{"total":0,"correct":0,"incorrect":0}');
  stats.total++;
  if (isCorrect) {
    stats.correct++;
  } else {
    stats.incorrect++;
  }
  localStorage.setItem("stats", JSON.stringify(stats));
};

onMounted(() => {
  loadWords();
});
</script>

<style scoped>
.game-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 0px 20px 20px;
}

.game-content {
  text-align: center;
  max-width: 500px;
  width: 100%;
}

.game-title {
  font-size: 3.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 60px;
  transition: color 0.3s ease;
}

.dark-mode .game-title {
  color: #e0e0e0;
}

.start-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.level-text {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
  transition: color 0.3s ease;
}

.dark-mode .level-text {
  color: #999;
}

.play-button {
  background-color: #1a1a1a;
  color: white;
  border: none;
  padding: 20px 80px;
  font-size: 1.3rem;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}

.play-button:hover {
  background-color: #333;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(107, 123, 247, 0.3);
}

.dark-mode .play-button {
  background-color: #e0e0e0;
  color: #0f0f0f;
}

.dark-mode .play-button:hover {
  background-color: #ffffff;
}

.game-screen {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.level-indicator {
  font-size: 1rem;
  color: #999;
  margin: 0;
  transition: color 0.3s ease;
}

.dark-mode .level-indicator {
  color: #666;
}

.word-display {
  font-size: 2.5rem;
  font-weight: 600;
  letter-spacing: 8px;
  color: #1a1a1a;
  padding: 30px;
  background: #f8f8f8;
  border-radius: 12px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.dark-mode .word-display {
  color: #e0e0e0;
  background: #1a1a1a;
}

.input-field {
  width: 100%;
  padding: 16px 20px;
  font-size: 1.1rem;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background-color: #ffffff;
  color: #1a1a1a;
}

.input-field:focus {
  border-color: #6b7bf7;
}

.dark-mode .input-field {
  background-color: #1a1a1a;
  color: #e0e0e0;
  border-color: #3a3a3a;
}

.dark-mode .input-field:focus {
  border-color: #6b7bf7;
}

.hint-text {
  padding: 10px 16px;
  background-color: #fff3cd;
  color: #856404;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
  transition: all 0.3s ease;
}

.dark-mode .hint-text {
  background-color: #3d3416;
  color: #ffc107;
}

.button-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.submit-button {
  background-color: #1a1a1a;
  color: white;
  border: none;
  padding: 16px 40px;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}

.submit-button:hover {
  background-color: #333;
  transform: translateY(-1px);
}

.dark-mode .submit-button {
  background-color: #e0e0e0;
  color: #0f0f0f;
}

.dark-mode .submit-button:hover {
  background-color: #ffffff;
}

.hint-button {
  background-color: #fff;
  border: 2px solid #e0e0e0;
  padding: 12.5px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hint-button:hover:not(:disabled) {
  border-color: #ffc107;
  background-color: #fffbf0;
  transform: translateY(-1px);
}

.dark-mode .hint-button {
  background-color: #1a1a1a;
  border-color: #3a3a3a;
}

.dark-mode .hint-button:hover:not(:disabled) {
  border-color: #ffc107;
  background-color: #2a2a1a;
}

.hint-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint-icon {
  width: 24px;
  height: 24px;
  display: block;
  transition: filter 0.3s ease;
}

.dark-mode .hint-icon {
  filter: brightness(0) invert(1);
}

.feedback {
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
  transition: all 0.3s ease;
}

.feedback.correct {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.dark-mode .feedback.correct {
  background-color: #1b3d1f;
  color: #81c784;
}

.feedback.incorrect {
  background-color: #ffebee;
  color: #c62828;
}

.dark-mode .feedback.incorrect {
  background-color: #3d1b1b;
  color: #e57373;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
